import {DocumentPreview} from "@/components/ui/forms/DocumentPreview";
import {MaskList} from "@/components/ui/forms/MaskList";

import {useCallback, useEffect, useRef, useState} from "react";

import {isEqual} from "@helpers";
import {useLevelOrder} from "@hooks/LevelOrder";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {
  clearEdit,
  getEditProduct,
  setError,
  setIsChanged,
  setTarget,
  updateProduct,
} from "@redux/slices/productSlices/editSlice";
import {
  clearOptions,
  getCountries,
  getProductLine,
} from "@redux/slices/productSlices/optionsSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {LevelDocuments} from "@ui/forms/LevelDocuments";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import styles from "./styles.module.scss";

export const ProductEditPage = () => {
  const {t} = useTranslation();
  const {openPopup} = usePopup();
  const {productLevels} = useLevelOrder();
  const {id} = useParams();
  const dispatch = useDispatch();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {countries, productLine} = useSelector(
    (state) => state.product.options,
  );
  const {isChanged, target, initialTarget, errors, status} = useSelector(
    (state) => state.product.edit,
  );

  const [groupedFiles, setGroupedFiles] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(false);

  const productRef = useRef();

  const updateProductField = useCallback(
    (fieldType, value) => {
      dispatch(
        setTarget({
          ...target,
          product: {
            ...target.product,
            [fieldType]: value,
          },
        }),
      );
    },
    [target, dispatch],
  );

  const handleClickCover = useCallback(
    (color) => updateProductField("mask", color),
    [updateProductField],
  );

  const handleClickRemovePhoto = () => {
    setDeletePhoto(true);
    setPhotoFile(null);
    updateProductField("cover", null);
  };

  const handleClickUploadImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  const handleClickSave = async () => {
    if (
      !target.product.mask ||
      !target.product.name ||
      !target.product.country_id ||
      !target.product.product_line_id
    ) {
      openPopup(<FillDataPopup />);
    } else {
      const product = {
        product: {
          name: target.product.name,
          country_id: target.product.country_id,
          product_line_id: target.product.product_line_id,
          cover: photoFile,
          remove_cover: deletePhoto,
          mask: target.product.mask,
        },
        product_version: {
          questions: target.product_version.questions,
        },
      };

      const resultAction = await dispatch(updateProduct({id, product}));

      if (updateProduct.fulfilled.match(resultAction)) {
        openPopup(<SuccessfulSavePopup title={t("popup.save_data.product")} />);

        URL.revokeObjectURL(photoFile);
        dispatch(setIsChanged(false));
        setPhotoFile(null);
        setDeletePhoto(false);
      } else {
        dispatch(setError("Save error!"));
      }
    }
  };

  useEffect(() => {
    if (target?.product_version?.files) {
      const filesByLevel =
        productLevels &&
        productLevels.map((level) => ({
          level: level.value,
          file: target.product_version.files.filter(
            (file) => file.level === level.value,
          ),
        }));

      setGroupedFiles(filesByLevel);
    }
  }, [target, productLevels]);

  useEffect(() => {
    if (target && initialTarget) {
      dispatch(setIsChanged(!isEqual(target, initialTarget)));
    }
  }, [target, initialTarget, dispatch]);

  useEffect(() => {
    dispatch(getEditProduct({id}));
    dispatch(getCountries());
    dispatch(getProductLine());

    return () => {
      dispatch(clearEdit());
      dispatch(clearOptions());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (productRef.current && isMobile) {
      productRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  useEffect(() => {
    if (photoFile) {
      dispatch(setIsChanged(true));
      setDeletePhoto(false);
    }

    return () => {
      if (photoFile) {
        URL.revokeObjectURL(photoFile);
        dispatch(setIsChanged(false));
        setDeletePhoto(false);
      }
    };
  }, [photoFile, target, initialTarget, dispatch]);

  return (
    <div
      ref={productRef}
      className={styles.productWrapper}
    >
      <BorderArticle>
        <div className={styles.product}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.productContent}>
              <div className={styles.productContentHeadWrapper}>
                <div className={styles.productEditContent}>
                  <div className={styles.productContentHead}>
                    <EditTitle
                      label={"product-name"}
                      value={target?.product?.name}
                      placeholder={t("input.product_name_placeholder")}
                      handleClickSave={handleClickSave}
                      disabledButton={!isChanged}
                      onChange={(e) =>
                        updateProductField("name", e.target.value)
                      }
                    />

                    <ViewField
                      label={`${t("input.background")}*`}
                      customContent={
                        <MaskList
                          onClickCover={(color) => handleClickCover(color)}
                        />
                      }
                    />

                    <ViewField
                      label={t("input.cover")}
                      customContent={
                        <div
                          className={"flex flex-col items-start gap-[16rem]"}
                        >
                          {(target?.product?.cover || photoFile) && (
                            <DocumentPreview
                              name={
                                photoFile?.name ||
                                target?.product?.cover_metadata.filename
                              }
                              onClickCross={() => handleClickRemovePhoto()}
                            />
                          )}

                          <ControlButton
                            color={"var(--violetLight)"}
                            txt={t("button.upload_image")}
                            onClick={handleClickUploadImage}
                          />
                        </div>
                      }
                    />
                  </div>

                  <ViewField
                    label={t("input.preview")}
                    direction={"col"}
                    customContent={
                      <PreviewCard
                        name={target?.product?.name}
                        mask={target?.product?.mask}
                        image={
                          photoFile
                            ? URL.createObjectURL(photoFile)
                            : target?.product?.cover || undefined
                        }
                      />
                    }
                  />
                </div>
              </div>

              <ul className={`${styles.productContentBody} ${styles.editBody}`}>
                <EditField
                  label={`${t("input.country")}*`}
                  width={250}
                  height={150}
                  searchPlaceholder={t("input.country_search")}
                  activeOption={target?.product?.country}
                  options={countries}
                  onSelect={(selectedOption) =>
                    updateProductField("country_id", selectedOption?.id)
                  }
                  type={"select"}
                />

                <EditField
                  label={`${t("input.product_line")}*`}
                  width={250}
                  height={150}
                  searchPlaceholder={t("input.product_line_search")}
                  activeOption={target?.product?.product_line}
                  options={productLine}
                  onSelect={(selectedOption) =>
                    updateProductField("product_line_id", selectedOption.id)
                  }
                  error={
                    errors && errors.product_line
                      ? isMobile
                        ? t("server_error.product_line_mismatch")
                        : t("server_error.product_line_mismatch_full")
                      : null
                  }
                  type={"select"}
                />

                <EditField
                  label={`${t("input.related_documents_view")}*`}
                  customContent={
                    <div className={styles.productLevelList}>
                      {groupedFiles.map((item) => (
                        <LevelDocuments
                          key={item.level}
                          level={item.level}
                          files={item.file}
                        />
                      ))}
                    </div>
                  }
                  type={"custom"}
                />

                <EditField
                  label={`${t("input.questions")}*`}
                  customContent={
                    <RootDesc>
                      <span
                        style={{color: "var(--grey)", pointerEvents: "none"}}
                      >
                        {target?.product_version?.questions || ""}
                      </span>
                    </RootDesc>
                  }
                  type={"custom"}
                />
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
